import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { getJobs } from '../../api/jobs';
import { StatusBadge } from '../jobs/StatusBadge';
import { formatJobNumber } from '../../utils/constants';

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  // Dışarı tıklayınca kapat
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await getJobs({ search: query.trim(), limit: 8 });
        setResults(data.jobs);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const handleSelect = (job) => {
    setQuery('');
    setOpen(false);
    navigate(`/jobs?jobId=${job.id}`);
  };

  return (
    <div ref={wrapRef} className="relative w-72">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder="İş no, başlık, müşteri, personel..."
        className="w-full pl-8 pr-8 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      />
      {query && (
        <button onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
          <X size={13} />
        </button>
      )}

      {open && (
        <div className="absolute top-full mt-2 left-0 w-96 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
          {loading && (
            <div className="px-4 py-3 text-sm text-slate-400 flex items-center gap-2">
              <div className="w-3.5 h-3.5 border border-blue-400 border-t-transparent rounded-full animate-spin" />
              Aranıyor...
            </div>
          )}
          {!loading && results.length === 0 && (
            <p className="px-4 py-3 text-sm text-slate-400">Sonuç bulunamadı</p>
          )}
          {!loading && results.map(job => (
            <button
              key={job.id}
              onClick={() => handleSelect(job)}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
            >
              <span className="text-xs font-mono text-slate-400 shrink-0 w-20">
                {job.job_number ? formatJobNumber(job.job_number) : '—'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{job.title}</p>
                {(job.customer_name || job.assigned_to_name) && (
                  <p className="text-xs text-slate-400 truncate">
                    {[job.customer_name, job.assigned_to_name].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <StatusBadge status={job.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
